import { useEffect, useRef } from "react";

/** Press-and-hold duration before the quick-action menu opens. 500ms is the
 * Android long-press convention the reference apps follow — shorter fires
 * during ordinary scrolling, longer feels broken. */
export const LONG_PRESS_MS = 500;
/** Movement that turns a press into a scroll (matches touchScroll's axis lock). */
const PRESS_SLOP_PX = 8;

const isOk = (key: string) => key === "Enter" || key === " ";

/**
 * Swallow the rest of a held key: every further keydown and the keyup that ends it.
 *
 * WHY. The hold opens a menu, the menu takes focus onto its first item (Watch),
 * and the remote is still sending OK — so the next repeat pressed Watch, and
 * Watch closed the menu. The viewer saw it for half a second (owner,
 * 2026-09-24: "it pops up for a half a second and disappears"). The press that
 * opened the menu is spent; nothing may act on it until the key is let go.
 *
 * On the window, in the capture phase, so it runs before React's own listener
 * and before the focused button's default activation.
 */
export function swallowHeldKey(key: string): void {
  let silence = 0;
  const stop = () => {
    window.removeEventListener("keydown", swallow, true);
    window.removeEventListener("keyup", swallow, true);
    window.clearTimeout(silence);
  };
  // A held key repeats every ~50-140 ms (measured on the projector), so a gap
  // longer than this means it is up even if its keyup never arrived (focus
  // left the WebView mid-hold) — OK must not stay dead for the session.
  const listen = () => {
    window.clearTimeout(silence);
    silence = window.setTimeout(stop, HELD_KEY_SILENCE_MS);
  };
  function swallow(event: KeyboardEvent) {
    if (event.key !== key) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.type === "keyup") stop();
    else listen();
  }
  window.addEventListener("keydown", swallow, true);
  window.addEventListener("keyup", swallow, true);
  listen();
}

/** How long without a repeat before a held key counts as released. */
export const HELD_KEY_SILENCE_MS = 400;

type Point = { x: number; y: number };

/**
 * Tap to act, hold to open the quick-action menu — by pointer, right-click, or
 * a remote's OK. Shared by the guide's programme cells and its channel rows so
 * both behave the same way.
 *
 * OK RESOLVES ON KEY UP: a short press acts, a held one (LONG_PRESS_MS) opens
 * the menu. Whether the key is still down is tracked HERE rather than read
 * from `event.repeat`, because the Android WebView does not set it: measured on
 * the projector 2026-09-24, every repeat of a held OK arrived with
 * `repeat === false`, so each one restarted the timer as a fresh press and the
 * menu never opened while the key was held.
 */
export function usePressAndHold(onPress: () => void, onHold?: (point: Point) => void) {
  const timer = useRef<number | null>(null);
  // Set when a hold fires so the click/keyup that ends it does not also act.
  const suppress = useRef(false);
  // OK is down on THIS element: its repeats are the same press, not new ones.
  const okDown = useRef(false);
  const pressStart = useRef<Point>({ x: 0, y: 0 });

  const cancel = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(() => cancel, []);

  const arm = (point: Point, key: string | null) => {
    cancel();
    suppress.current = false;
    if (!onHold) return;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      suppress.current = true;
      if (key) {
        okDown.current = false;
        swallowHeldKey(key);
      }
      onHold(point);
    }, LONG_PRESS_MS);
  };

  return {
    onClick: () => {
      if (suppress.current) { suppress.current = false; return; }
      onPress();
    },
    onPointerDown: (e: React.PointerEvent) => {
      if (!onHold || e.button !== 0) return;
      pressStart.current = { x: e.clientX, y: e.clientY };
      arm({ x: e.clientX, y: e.clientY }, null);
    },
    // A finger that travels is scrolling the guide, not holding: vertical
    // drags are handled in script (touchScroll.ts), so the browser never
    // cancels the pointer for us the way a native pan does.
    onPointerMove: (e: React.PointerEvent) => {
      if (timer.current === null) return;
      if (Math.hypot(e.clientX - pressStart.current.x, e.clientY - pressStart.current.y) > PRESS_SLOP_PX) cancel();
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    // Right-click on desktop, and the system long-press gesture on Android,
    // both surface here — same menu, no duplicate timer.
    onContextMenu: (e: React.MouseEvent) => {
      if (!onHold) return;
      e.preventDefault();
      cancel();
      onHold({ x: e.clientX, y: e.clientY });
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (!isOk(e.key)) return;
      e.preventDefault();
      if (okDown.current || e.repeat) return; // the same press, still held
      okDown.current = true;
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      arm({ x: r.left + r.width / 2, y: r.top + r.height / 2 }, e.key);
    },
    onKeyUp: (e: React.KeyboardEvent) => {
      if (!isOk(e.key)) return;
      e.preventDefault();
      // A release whose press this element never saw — OK pressed on a dialog
      // that closed and handed focus here — is not a press on this element.
      if (!okDown.current) return;
      okDown.current = false;
      cancel();
      if (suppress.current) { suppress.current = false; return; }
      onPress();
    },
    // Focus moved away mid-press: that press is over.
    onBlur: () => {
      okDown.current = false;
      cancel();
    },
  };
}
